/**
 * Symphony GROUP CHAT — what a frame on the member-scoped `individuals-group-chat` channel carries, decided without
 * React or fetch so the hook (`lib/use-group-chat-session.ts`) only moves data. iOS `GroupRealtime` and Android
 * `GroupRealtime` read the same frame the same way.
 *
 * The server (client-sync `fanOutGroupPost`, `ClientPayloads.conversationMessage`) publishes
 * `{entityId: conversationId, entity: <the ConversationMessage itself>}` — the entity is the message, NOT wrapped in a
 * `conversationMessage` key. Reading only `entity.conversationMessage` dropped every live frame, so an open group chat
 * showed others' messages, votes, pins and Maestro replies only after it was reopened. Both shapes are read.
 */
import { resolveAuthor, type GroupEvent, type GroupMessageMeta, type GroupSummary, type MessageAuthor } from './group-chat';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * The ConversationMessage a `created` / `updated` frame's `entity` carries: `entity.conversationMessage` when that is an
 * object (the wrapped shape), else the entity itself when it looks like a message (it has an `id` or a
 * `conversationId`). Null for anything else.
 */
export function realtimeMessageWire(entity: unknown): Record<string, unknown> | null {
  if (!isObject(entity)) return null;
  if (isObject(entity.conversationMessage)) return entity.conversationMessage;
  if (typeof entity.id === 'string' || typeof entity.conversationId === 'string') return entity;
  return null;
}

/**
 * Whether a live row changes WHO is in the group, so the participants (the `@` picker, the info screen, the header)
 * must be re-read now rather than on the next open: a membership line (joined, left, a Maestro added or removed), a
 * MEMBERS_ADDED system line, and every Phase 10a info line (a member joined by link, removed, a setting changed).
 */
export function realtimeRefreshesParticipants(row: { groupEvent?: GroupEvent | null; groupMeta?: Pick<GroupMessageMeta, 'membersAdded' | 'infoEvent'> | null }): boolean {
  return !!row.groupEvent || !!row.groupMeta?.membersAdded || !!row.groupMeta?.infoEvent;
}

/**
 * The group whose members a `created` frame makes the client re-read, or null (2026-10-31 rerun): a frame that does
 * NOT parse as a message (a membership change published without a row — a Maestro joining is one) re-reads the
 * group it names, and so does a parsed row that changes who is in the group (`realtimeRefreshesParticipants`). The
 * chats list is re-read on every frame regardless. iOS / Android `GroupRealtime.membersRefreshTarget` decide the same.
 */
export function membersRefreshTarget(
  entityId: string | null | undefined,
  row: { conversationId?: string; author?: MessageAuthor | null; groupEvent?: GroupEvent | null; groupMeta?: Pick<GroupMessageMeta, 'membersAdded' | 'infoEvent'> | null } | null,
  known?: GroupSummary | null,
): string | null {
  if (!row) return entityId || null;
  if (!realtimeRefreshesParticipants(row) && !authorUnknownTo(known, row.author)) return null;
  return row.conversationId || entityId || null;
}

/**
 * A message from a person or a Maestro the held participants do not name (2026-11-01 hands-on: a member who did not add
 * the Maestro kept a list without it, so its replies read "Maestro" instead of "Caro's Maestro"): the participants are
 * stale, re-read them. False with no summary held (the open of that chat reads them) and for SYSTEM rows.
 */
export function authorUnknownTo(known: GroupSummary | null | undefined, author: MessageAuthor | null | undefined): boolean {
  if (!known || !author || (author.kind !== 'HUMAN' && author.kind !== 'MAESTRO')) return false;
  return !resolveAuthor(known, author);
}

/**
 * What an `ended` frame (VMR #34789: `{entityId: conversationId, entity: {conversationId, groupEnded: true,
 * conversationMessage}}`, sent to every member the end removed and the owner) names: the group's conversation — the
 * entity's `conversationId`, else the frame's `entityId` — and the GROUP_ENDED line when it rides along. Null when
 * neither names a conversation. iOS / Android `groupEndedTarget` decide the same.
 */
export function groupEndedTarget(entityId: string | null | undefined, entity: unknown): { conversationId: string; message: Record<string, unknown> | null } | null {
  const fromEntity = isObject(entity) && typeof entity.conversationId === 'string' && entity.conversationId ? entity.conversationId : null;
  const conversationId = fromEntity ?? (entityId || null);
  if (!conversationId) return null;
  const message = isObject(entity) && isObject(entity.conversationMessage) ? entity.conversationMessage : null;
  return { conversationId, message };
}

/**
 * Design §21: the message a live `created` row acks as DELIVERED to this device — any row from someone else (a person,
 * a Maestro, a system line), never the viewer's own post and never a tombstone. `viewerId` unknown → acked (a
 * delivered position through one's own message is harmless). iOS / Android `GroupRealtime.deliveredAckTarget` decide
 * the same.
 */
export function deliveredAckTarget(
  row: { id?: string; finalId?: string; createdDate?: string; author?: MessageAuthor | null; groupMeta?: { deleted?: unknown } | null },
  viewerId: string | null | undefined,
): { messageId: string; createdAt: string | null } | null {
  const messageId = row.finalId ?? row.id;
  if (!messageId || row.groupMeta?.deleted) return null;
  if (row.author?.kind === 'HUMAN' && viewerId && row.author.principalMetaSiteId === viewerId) return null;
  return { messageId, createdAt: row.createdDate ?? null };
}

/**
 * Design §21: the message an open JOINED group marks read through — the window's newest (`windowNewest`), or the
 * newest row that arrived LIVE when it is newer than that (it is not in the window until the next re-read). A live
 * row the window already holds, a pending post or a local ENDED line never moves it.
 */
export function newestReadThroughId(
  window: ReadonlyArray<{ id?: string; finalId?: string; createdDate?: string }>,
  windowNewest: string | null,
  lastLive: { id?: string; createdDate?: string; groupPost?: unknown } | null | undefined,
): string | null {
  const liveId = lastLive?.id;
  if (!lastLive || !liveId || lastLive.groupPost || liveId.startsWith('optimistic-') || liveId.startsWith('group-ended-')) return windowNewest;
  if (!windowNewest) return liveId;
  if (window.some((m) => (m.finalId ?? m.id) === liveId)) return windowNewest;
  const held = window.find((m) => (m.finalId ?? m.id) === windowNewest);
  const liveAt = lastLive.createdDate ? Date.parse(lastLive.createdDate) : Number.NaN;
  const heldAt = held?.createdDate ? Date.parse(held.createdDate) : Number.NaN;
  if (Number.isFinite(liveAt) && Number.isFinite(heldAt) && liveAt < heldAt) return windowNewest;
  return liveId;
}
